// validations.fake.service.ts
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { delay, Observable, of, throwError } from 'rxjs';
import { ServiceType } from 'src/app/core/constants/service-type.constant';
import {
  IIncodeStatusResponseApi,
  ILoginIncodeResponse,
} from '../../interfaces/incode.interface';
import {
  ClientInfoResponse,
  Compatibility,
  DnValidation,
  IContractBody,
  IContractResponse,
  IDn,
  IInfoClientAvailableCredit,
  IResponseAvailableCredit,
  IResponseDnProfile,
  IResponseSaveInfoClient,
  ISaveClientInfo,
  ISaveSignature,
  IValidationDnTypeService,
  IZipCode,
  OtpResponse,
  OtpResponseValidation
} from '../../interfaces/validations.interface';
import { ValidationsServiceBase } from './validations.base';

@Injectable({ providedIn: 'root' })
export class FakeValidationsService extends ValidationsServiceBase {
  ValidateDn(dn: DnValidation): Observable<OtpResponse> {
    if (dn.dn.endsWith('0504'))
      return throwError(() => new HttpErrorResponse({ status: 504 })).pipe(
        delay(1000),
      );
    if (dn.dn.endsWith('0409'))
      return throwError(
        () =>
          new HttpErrorResponse({
            error: { information: 'Límite OTP' },
            status: 409,
          }),
      ).pipe(delay(1000));

    return of({ success: true, data: 'fake_idDn_123', otp: '123456' }).pipe(
      delay(1000),
    );
  }

  validateOtp(
    code: string,
    idDn: string,
    imei: string,
  ): Observable<OtpResponseValidation> {
    if (code !== '123456') {
      return throwError(
        () =>
          new HttpErrorResponse({
            error: { message: 'Código inválido' },
            status: 400,
          }),
      ).pipe(delay(1000));
    }
    return of({
      auth: {
        key: 'fake_session_id_123',
        url: 'https://identity-qa.ordenaris.com/fake-url',
      },
      identifier: 999,
    }).pipe(delay(1000));
  }

  validateDnType(dn: string): Observable<IValidationDnTypeService> {
    if (dn.endsWith('1111'))
      return of({ success: true, data: 'OK' }).pipe(delay(800));
    if (dn.endsWith('2222'))
      return throwError(() => new HttpErrorResponse({ status: 412 })).pipe(
        delay(800),
      );
    return throwError(() => new HttpErrorResponse({ status: 500 })).pipe(
      delay(800),
    );
  }

  checkCompatibility(imei: string): Observable<Compatibility> {
    if (imei.endsWith('1111')) {
      return of({
        success: true,
        supportEsim: true,
        homologated: true,
      }).pipe(delay(1000));
    }

    if (imei.endsWith('0007')) {
      return of({
        success: true,
        supportEsim: false,
        homologated: false,
      }).pipe(delay(1000));
    }

    if (imei.endsWith('0005')) {
      return throwError(
        () =>
          new HttpErrorResponse({
            error: {
              mensaje:
                'Estimado usuario, no se ha encontrado información sobre este IMEI.',
            },
            status: 404,
          }),
      ).pipe(delay(1000));
    }

    if (imei.endsWith('0004')) {
      return throwError(
        () =>
          new HttpErrorResponse({
            error: {
              mensaje:
                'Estimado usuario, es necesario que descargues en tu dispositivo la aplicación VozAPP...',
            },
            status: 400,
          }),
      ).pipe(delay(1000));
    }

    return of({
      success: true,
      supportEsim: false,
      homologated: true,
    }).pipe(delay(1000));
  }

  validateOtpPortability(
    code: string,
    idDn: string,
  ): Observable<OtpResponseValidation> {
    return of({
      auth: { key: 'fake_key_port', url: 'https://fake.url/port' },
      identifier: 888,
    }).pipe(delay(1000));
  }

  getClientInfo(sessionId: string): Observable<ClientInfoResponse> {
    return of({
      success: true,
      data: {
        address: {
          settlement: 'Juárez',
          state: 'Ciudad de México',
          street: 'Av. Paseo de la Reforma',
          town: 'Cuauhtémoc',
          zipCode: '06500',
        },
        age: 37,
        birthDay: '1989-06-02',
        dateOfExpiry: '2030-12-31',
        documentNumber: 'ID987654321',
        firstName: 'Liam',
        gender: 'M',
        lastName: 'Díaz Guerrero',
        nationality: 'MEX',
        personalNumber: '',
        registryCode: 'PEGJ900101HDFRRN10',
        taxIdNumber: 'XAXX010101000',
        voterNumber: 'VOTER98765',
      },
    } as ClientInfoResponse).pipe(delay(1000));
  }

  getColony(colony: string): Observable<IZipCode> {
    if (colony === '00000') {
      return throwError(() => new HttpErrorResponse({ status: 404 })).pipe(
        delay(800),
      );
    }

    const zipCodeToUse = colony || '06500';

    return of<IZipCode>({
      success: true,
      municipality: [{ id: '1', name: 'Cuauhtémoc' }],
      data: {
        country: { uuid: 'c1', name: 'México' },
        state: { uuid: 's1', name: 'Ciudad de México' },
        settlements: [
          {
            name: 'Colonia Centro',
            uuid: 'set1',
            zipCode: zipCodeToUse,
            city: { uuid: 'city1', name: 'CDMX' },
            municipality: { uuid: 'mun1', name: 'Cuauhtémoc' },
          },
          {
            name: 'Colonia Roma',
            uuid: 'set2',
            zipCode: zipCodeToUse,
            city: { uuid: 'city1', name: 'CDMX' },
            municipality: { uuid: 'mun1', name: 'Cuauhtémoc' },
          },
          {
            name: 'Colonia Condesa',
            uuid: 'set3',
            zipCode: zipCodeToUse,
            city: { uuid: 'city1', name: 'CDMX' },
            municipality: { uuid: 'mun1', name: 'Cuauhtémoc' },
          },
        ],
      },
    }).pipe(delay(800));
  }

  profileDn(value: IDn, type: string): Observable<IResponseDnProfile> {
    if (value.value.endsWith('0412')) {
      return throwError(() => new HttpErrorResponse({ status: 412 })).pipe(
        delay(1000),
      );
    }

    // No valido
    if (value.value.endsWith('0006')) {
      return of({
        success: true,
        data: {
          msisdn: value.value,
          imsi: 'fake_imsi_123',
          iccid: 'fake_iccid_123',
          status: 6,
        },
      }).pipe(delay(800));
    }

    // Migrado
    if (value.value.endsWith('0000')) {
      return of({
        success: true,
        data: {
          msisdn: value.value,
          imsi: 'fake_imsi_123',
          iccid: 'fake_iccid_123',
          status: 0,
        },
      }).pipe(delay(800));
    }

    // Nuevo
    return of({
      success: true,
      data: {
        msisdn: value.value,
        imsi: 'fake_imsi_123',
        iccid: '895214fake123',
        status: 3,
      },
    }).pipe(delay(800));
  }

  saveClientInfo(
    dataClient: ISaveClientInfo,
  ): Observable<IResponseSaveInfoClient> {
    return of({ success: true, data: { identifier: 'client_123' } }).pipe(
      delay(1500),
    );
  }

  saveClientPortabilityInfo(
    dataClient: ISaveClientInfo,
  ): Observable<IResponseSaveInfoClient> {
    return of({ success: true, data: { identifier: 'client_port_123' } }).pipe(
      delay(1500),
    );
  }

  getOffersAvailableCredit(
    code: string,
    idDn: string,
    clientInfo: IInfoClientAvailableCredit,
    classification: string,
  ): Observable<IResponseAvailableCredit> {
    return of({
      success: true,
      information: {
        uuid: '18a544c6ae0011efaa830242ac123909',
        description: 'Excelente',
        risk: 5,
        downpayment: 0,
        bail: 150,
      },
      listOfOffers: [
        {
          offeringId: '1609902676',
          name: 'Plan Bait Salud',
          image:
            'https://baitqa.ordenaris.com/cards/v5/mbb/pospaid/app-249-card.webp',
          include: 'Plan Bait Salud',
          enjoy: 'Obtén internet ilimitado...',
          duration: 30,
          price: 249,
          type: 1,
          uuid: '26f079b54ce343bf932c8ef63d7a35a3',
        },
        {
          offeringId: '1609902678',
          name: "Plan Bait Sam's Benefits",
          image:
            'https://baitqa.ordenaris.com/cards/v5/mbb/pospaid/app-339-card.webp',
          include: "Plan Bait Sam's Benefits",
          enjoy: 'Obten internet ilimitado...',
          duration: 30,
          price: 339,
          type: 1,
          uuid: 'ddedb60ad6b8490687190cad5ae26608',
        },
      ],
    }).pipe(delay(1000));
  }

  getInfoContract(
    offer: IContractBody,
    uuidPreregister: string,
  ): Observable<IContractResponse> {
    return of({
      uuid: 'contract_123',
      noContract: 'CTR-001',
      noClient: 'CLI-001',
      dateContract: new Date().toISOString(),
      firstName: 'Juan',
      lastName: 'Pérez',
      middleName: '',
      dn: null,
      contactPhone: '5555555555',
      email: 'test@test.com',
      numberINE: 'INE123',
      imei: 'IMEI123',
      offering: {} as any,
      address: {} as any,
    }).pipe(delay(1000));
  }

  saveLegalsSignature(
    signature: ISaveSignature,
    uuid: string,
  ): Observable<Blob> {
    if (uuid === 'fake_uuid_error_409') {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 409,
            error: { message: 'El contrato ya fue firmado previamente.' },
          }),
      ).pipe(delay(1500));
    }

    const fakePdfContent =
      'Este es un PDF simulado para pruebas de desarrollo local.';
    const fakeBlob = new Blob([fakePdfContent], { type: 'application/pdf' });

    return of(fakeBlob).pipe(delay(2000));
  }

  viewPortability(
    uuid: string,
  ): Observable<{ success: boolean; message?: string }> {
    if (uuid === 'fake_uuid_error_portabilidad') {
      return throwError(() => ({
        error: {
          message:
            'No se pudo iniciar el proceso de portabilidad con el proveedor.',
        },
      })).pipe(delay(1000));
    }

    return of({
      success: true,
      message: 'Portabilidad iniciada correctamente',
    }).pipe(delay(1000));
  }

  noPaymentContract(
    uuidContract: string,
    typeService: ServiceType,
  ): Observable<{ success: boolean; message?: string }> {
    if (uuidContract === 'fake_uuid_error_no_payment') {
      return throwError(() => ({
        error: {
          message: 'Ocurrió un error al activar la línea en el sistema.',
        },
      })).pipe(delay(1000));
    }
    return of({
      success: true,
      message: `Contrato ${typeService} activado exitosamente sin requerir pago.`,
    }).pipe(delay(1500));
  }

  getValidateStepIncode(
    sessionId: string,
    token: string,
  ): Observable<IIncodeStatusResponseApi> {
    return of({
      success: true,
      data: {
        status: {
          onboardingStatus: 'ONBOARDING_FINISHED',
        },
      },
    } as IIncodeStatusResponseApi).pipe(delay(500));
  }

  loginIncode(): Observable<ILoginIncodeResponse> {
    return of({
      success: true,
      accessToken: 'fake_incode_access_token',
    } as any).pipe(delay(1000));
  }
}
